import moment from "moment";
import {
  getLoa,
  cancelLoa,
} from "../../api/loaApi";
import {getEmployees} from "../../api/employeeApi";
import {getLiftoffMapping} from "../../api/liftoffMappingApi";
import {useUser} from "../../auth/userHooks";
import {useState, useEffect} from "react";
import {
  Button,
  Col,
  Input,
  PageHeader,
  Row,
  Table,
  Checkbox,
  Popconfirm,
} from "antd";
import {
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  PREFERRED_DATE_FORMAT,
  PREFERRED_DATETIME_FORMAT,
} from "../../util/time";
import _ from "lodash";
import sorters from "../../util/sorters";
import LoaModal from "./LoaModal";
import {getPaginationConfig} from "../../util/showAllPagination";

const LeaveOfAbsenceTable = () => {
  const user = useUser();
  const [loaRecords, setLoaRecords] = useState([]);
  const [filteredLoaRecords, setFilteredLoaRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAuditing, setShowAuditing] = useState(false);
  const [warning, setWarning] = useState("");
  const [recordToEdit, setRecordToEdit] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [employeeResourceTypes, setEmployeeResourceTypes] = useState([]);
  const [mapLeaveResourceTypes, setMapLeaveResourceTypes] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      Promise.all([
        /* #0 */ getLoa(),
        /* #1 */ getLiftoffMapping("projector_resource_types"),
        /* #2 */ getLiftoffMapping("projector_leave_types"),
        /* #3 */ getLiftoffMapping("projector_map_employee_resource_type"),
        /* #4 */ getEmployees(),
        /* #5 */ getLiftoffMapping("projector_map_leave_resource_types")
      ])
        .then(results => {
          setLoaRecords(results[0]);
          filterLoaRecords(results[0], searchText);
          findDuplicates(results[0]);
          setResourceTypes(results[1]);
          setLeaveTypes(results[2]);
          // Only include employees that have a resource type and not a leave resource type, and if they are in
          // leave summary, then they need to have status COMPLETED or CANCELED
          const leaveResourceTypeIdList = [...new Set(results[5].map(item => item.LeaveResourceTypeId))];
          const filteredEmployees = results[4].filter((employee) =>
            results[3].some(
              (resourceType) => {
                const employeeLoas = results[0].filter(loa => loa.EmployeeId === employee.ID &&
                  loa.Status !== "COMPLETED" && loa.Status !== "CANCELED");
                return resourceType.EmployeeId === employee.ID &&
                  !leaveResourceTypeIdList.includes(resourceType.ResourceTypeId) &&
                  (!employeeLoas || employeeLoas.length === 0);
              }
            )
          );
          setEmployees(filteredEmployees);
          setEmployeeResourceTypes(results[3]);
          setMapLeaveResourceTypes(results[5]);

        })
        .catch(e => {
          console.error("Failed to fetch data:", e);
        })

    } catch (e) {
      console.error(`Could not get loa data: ${e}`);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Name",
      sorter: (a, b) => a.Employee.FullName.localeCompare(b.Employee.FullName),
      render: (record) => record.Employee.FullName,
      width: 90,
    },
    {
      title: "Email",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.Employee.Email.localeCompare(b.Employee.Email),
      render: (record) => record.Employee.Email,
      width: 110,
    },
    {
      title: "Resource Type",
      dataIndex: "ResourceType",
      sorter: (a, b) =>
        sorters.string(a.ResourceType.Name, b.ResourceType.Name),
      render: (record) => record.Name,
      width: 100,
    },
    {
      title: "Post Resource Type",
      dataIndex: "PostResourceType",
      sorter: (a, b) =>
        sorters.string(a.PostResourceType.Name, b.PostResourceType.Name),
      render: (record) => record.Name,
      width: 100,
    },
    {
      title: "Leave Type",
      dataIndex: "LeaveType",
      sorter: (a, b) => sorters.string(a.LeaveType.Name, b.LeaveType.Name),
      render: (record) => record.Name,
      width: 100,
    },
    {
      title: "Leave Start Date",
      dataIndex: "LeaveStartDate",
      sorter: (a, b) => sorters.date(a.LeaveStartDate, b.LeaveStartDate),
      render: (record) => moment.utc(record).format(PREFERRED_DATE_FORMAT),
      width: 95,
    },
    {
      title: "Leave End Date",
      dataIndex: "LeaveEndDate",
      sorter: (a, b) => sorters.date(a.LeaveEndDate, b.LeaveEndDate),
      render: (record) =>
      record === null ? "-" : moment.utc(record).format(PREFERRED_DATE_FORMAT),
      width: 95,

    },
    {
      title: "Total Leave Hours",
      sorter: (a, b) => sorters.number(a.TotalLeaveHours, b.TotalLeaveHours),
      render: (record) =>
        record.TotalLeaveHours === 0 ? "-" : record.TotalLeaveHours,
      width: 70,
    },
    {
      title: "Leave Balance",
      sorter: (a, b) => sorters.number(a.LeaveBalance, b.LeaveBalance),
      dataIndex: "LeaveBalance",
      width: 80,
    },
    {
      title: "Scheduled Time Off",
      sorter: (a, b) => sorters.number(a.ScheduledTimeOff, b.ScheduledTimeOff),
      dataIndex: "ScheduledTimeOff",
      width: 100,
    },
    {
      title: "Automatically Schedule Time",
      sorter: (a, b) =>
        a.AutoScheduleTime === b.AutoScheduleTime
          ? 0
          : a.AutoScheduleTime
            ? -1
            : 1,
      render: (record) => (record.AutoScheduleTime ? "Yes" : "No"),
      width: 90,
    },
    {
      title: "Status",
      sorter: (a, b) => sorters.string(a.Status, b.Status),
      dataIndex: "Status",
      width: 90,
    },
    {
      title: "Created By",
      dataIndex: "CreatedBy",
      sorter: (a, b) => sorters.string(a.CreatedBy, b.CreatedBy),
      hidden: !showAuditing,
      width: 95,
    },
    {
      title: "Created At",
      render: (r) =>
        moment.utc(r.CreatedAt).local().format(PREFERRED_DATETIME_FORMAT),
      sorter: (a, b) => sorters.date(a.CreatedAt, b.CreatedAt),
      hidden: !showAuditing,
      width: 95,
    },
    {
      title: "Updated By",
      render: (r) => (r.UpdatedBy ? r.UpdatedBy : "-"),
      sorter: (a, b) => sorters.string(a.UpdatedBy, b.UpdatedBy),
      hidden: !showAuditing,
      width: 95,
    },
    {
      title: "Updated At",
      render: (r) =>
        r.UpdatedAt
          ? moment.utc(r.UpdatedAt).local().format(PREFERRED_DATETIME_FORMAT)
          : "-",
      sorter: (a, b) => sorters.date(a.UpdatedAt, b.UpdatedAt),
      hidden: !showAuditing,
      width: 95,
    },
    {
      title: "Actions",
      fixed: 'right',
      render: (record) => (
        <>
          <Button
            onClick={() => editRecord(record)}
            icon={<EditOutlined/>}
            disabled={
              record.Status !== "SCHEDULED"
            }
            style={{marginRight: 10, marginBottom: 5}}
          >
            Edit
          </Button>
          <Popconfirm
            title={`Are you sure you want to cancel this leave ${
              record.Employee.FullName === undefined
                ? "?"
                : "for " + record.Employee.FullName + "?"
            }`}
            onConfirm={() => cancelRecord(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              icon={<DeleteOutlined/>}
              disabled={
                record.Status !== "SCHEDULED" && record.Status !== "IN_PROGRESS" && record.Status !== "PROJECTOR"
              }
            >
              Cancel
            </Button>
          </Popconfirm>
        </>
      ),
      width: 110,
    },
  ].filter((item) => !item.hidden);

  const editRecord = (record) => {
    setShowEditModal(true);
    setRecordToEdit(record);
  };

  const cancelRecord = async (record) => {
    try {
      setLoading(true);
      await cancelLoa(record.ID);
      await load();
    } catch (e) {
      console.error(`Could not cancel loa record: ${e}`);
    } finally {
      setLoading(false);
    }
  };


  const filterLoaRecords = (records, searchText) => {
    setFilteredLoaRecords(
      _.chain(records)
        .filter(
          (u) =>
            searchText === "" ||
            _.includes(_.toLower(u.Employee.FullName), _.toLower(searchText))
        )
        .value()
    );
  };

  const search = (text) => {
    setSearchText(text);
    filterLoaRecords(loaRecords, text);
  };

  const findDuplicates = (data) => {
    let warning = "";
    let dupes = _.chain(data)
      .map((d) => {
        return [
          d.Employee.FullName,
          d.ResourceTypeId,
          d.LeaveTypeId,
          moment.utc(d.LeaveStartDate).format(PREFERRED_DATE_FORMAT),
          moment.utc(d.LeaveEndDate).format(PREFERRED_DATE_FORMAT),
          d.TotalLeaveHours,
          d.LeaveBalance,
          d.ScheduledTimeOff,
        ];
      })
      .groupBy()
      .filter((v) => v.length > 1)
      .value();
    if (dupes.length > 0) {
      let names = dupes.flatMap((d) => d[0][0]);
      warning = `You have duplicates for ${
        dupes.length === 1 ? "employee" : "employees"
      } ${[...new Set(names)].join(", ")}.`;
    }
    setWarning(warning.trim());
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Row>
        <Col span={12}>
          <PageHeader
            title="Leave of Absence"
            subTitle={`(${filteredLoaRecords.length} shown)`}
            style={{padding: 0}}
          />
        </Col>
      </Row>

      <Row style={{marginBottom: 10}}>
        <Col span={9} style={{marginRight: 10}}>
          <div style={{display: "flex", alignItems: "center"}}>
            <Input
              placeholder="Search for employee(s) by name"
              prefix={<SearchOutlined/>}
              onChange={(e) => search(e.target.value)}
              allowClear
              style={{flex: 1}}
            />
            <Checkbox
              style={{marginLeft: 10}}
              checked={showAuditing}
              onChange={(e) => setShowAuditing(e.target.checked)}
            >
              Show Auditing
            </Checkbox>
          </div>
        </Col>
        <Col flex="auto">
          <Button
            style={{float: "right"}}
            onClick={() => load()}
            loading={loading}
            icon={<ReloadOutlined/>}
          >
            Refresh
          </Button>
          {user?.permissions?.canManageLoa && (
            <Button
              style={{float: "right", marginRight: 8}}
              onClick={() => setShowCreateModal(true)}
              icon={<PlusOutlined/>}
            >
              Create Leave of Absence
            </Button>
          )}
        </Col>
      </Row>
      <div style={{marginTop: 5, marginBottom: 5, color: "#ff4d4f"}}>
        {warning}
      </div>

      <Table
        rowKey={(record) => record.ID}
        dataSource={filteredLoaRecords}
        scroll={{x: 1500}}
        columns={columns}
        pagination={getPaginationConfig(filteredLoaRecords.length)}
        size="small"
        loading={loading}
      />
      {showEditModal && (
        <LoaModal
          isEditMode={true}
          open={showEditModal}
          onCancel={() => setShowEditModal(false)}
          record={recordToEdit}
          load={load}
          employees={employees}
          resourceTypes={resourceTypes}
          leaveTypes={leaveTypes}
          employeeResourceTypes={employeeResourceTypes}
        />
      )}
      {showCreateModal && (
        <LoaModal
          isEditMode={false}
          open={showCreateModal}
          onCancel={() => setShowCreateModal(false)}
          record={null}
          load={load}
          employees={employees}
          resourceTypes={resourceTypes}
          leaveTypes={leaveTypes}
          employeeResourceTypes={employeeResourceTypes}
          mapLeaveResourceTypes={mapLeaveResourceTypes}
        />
      )}
    </>
  );
};

export {LeaveOfAbsenceTable};
